import { useRef, useState } from "react"
import { Alert, Box, Button, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material"
import { If } from "lib/@components/switch"
import { Repeat } from "lib/@components/repeat"
import Iconify from "minimals-template/components/Iconify"
import { parseRateCardCsv } from "./import-rate-card-csv"
import { ImportCsvErrorRow } from "./import-csv-error-row"

export function ImportCsvDialog({ ok, cancel, card }) {
    const fileRef = useRef(null)
    const [parsed, setParsed] = useState(null)
    const [fileName, setFileName] = useState("")

    return (
        <Box data-cy="import-csv-dialog">
            <DialogTitle>Import CSV</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Upload a CSV file to update trade rates. Trade names are matched to existing trades in the skill
                    map.
                </Typography>
                <Button
                    variant="outlined"
                    component="label"
                    startIcon={<Iconify icon="solar:upload-bold" />}
                    data-cy="import-csv-file-button"
                >
                    Choose CSV File
                    <input
                        ref={fileRef}
                        type="file"
                        accept=".csv"
                        hidden
                        data-cy="import-csv-file-input"
                        onChange={handleFileChange}
                    />
                </Button>
                <If value={fileName}>
                    <Typography variant="body2" sx={{ mt: 1 }} data-cy="import-csv-file-name">
                        {fileName}
                    </Typography>
                </If>

                <If value={parsed}>
                    <Box sx={{ mt: 2 }}>
                        <If value={parsed?.errors?.length}>
                            <Alert severity="error" data-cy="import-csv-errors" sx={{ mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                    Errors ({parsed?.errors?.length})
                                </Typography>
                                <Repeat list={parsed?.errors || []} item={<ImportCsvErrorRow />} />
                            </Alert>
                        </If>

                        <If value={parsed?.warnings?.length}>
                            <Alert severity="warning" data-cy="import-csv-warnings" sx={{ mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                    Warnings ({parsed?.warnings?.length})
                                </Typography>
                                <Repeat list={parsed?.warnings || []} item={<ImportCsvErrorRow />} />
                            </Alert>
                        </If>

                        <If value={!parsed?.errors?.length}>
                            <Alert severity="success" data-cy="import-csv-preview" sx={{ mb: 1 }}>
                                {parsed?.lines?.length || 0} trade(s) will be updated
                            </Alert>
                        </If>
                    </Box>
                </If>
            </DialogContent>
            <DialogActions>
                <Button onClick={cancel} color="primary" data-cy="dialogActionsCancelButton">
                    Cancel
                </Button>
                <Button
                    onClick={handleImport}
                    variant="contained"
                    disabled={!parsed || Boolean(parsed?.errors?.length)}
                    data-cy="dialogActionsOkButton"
                >
                    Import
                </Button>
            </DialogActions>
        </Box>
    )

    function handleFileChange(e) {
        const file = e.target.files?.[0]
        if (!file) return
        setFileName(file.name)
        const reader = new FileReader()
        reader.onload = readComplete
        reader.readAsText(file)

        function readComplete(evt) {
            const text = evt.target.result
            const result = parseRateCardCsv(text, card?.lines || [])
            setParsed(result)
        }
    }

    function handleImport() {
        if (!parsed || parsed.errors?.length) return
        ok(parsed.lines)
    }
}
